import { useCallback, useEffect, useState } from "react";
import { History, LoaderCircle, Lock, ShieldCheck, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/adminRoles";

const ROLE_BADGES = {
  super_admin: "bg-bleu text-white",
  admin: "bg-bleu-light text-bleu",
  moderator: "bg-amber-100 text-amber-700",
};

export default function AdminSecurity() {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/security").then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger la sécurité."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (id, role) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/users/${id}`, { role });
      toast.success("Rôle mis à jour.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible.");
    } finally {
      setBusyId(null);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="page-admin-security">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Lock className="h-7 w-7 text-bleu" /> Sécurité
        </h1>
        <p className="mt-1 text-sm text-slate-500">Équipe d'administration, rôles et activité de sécurité. Réservé au Super Admin.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="card-integra p-5" data-testid="admin-security-attempts">
          <p className="font-display text-2xl font-extrabold text-slate-900">{data.login_attempts_24h}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tentatives de connexion échouées (24 h)</p>
        </div>
        <div className="card-integra p-5" data-testid="admin-security-audit-count">
          <p className="font-display text-2xl font-extrabold text-slate-900">{data.audit_total}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Actions tracées au journal d'audit</p>
        </div>
      </div>

      <section className="space-y-2.5" data-testid="admin-security-team">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <UserCog className="h-4 w-4 text-bleu" /> Équipe d'administration
        </h2>
        {data.team.map((u) => (
          <div key={u.id} className="card-integra flex flex-wrap items-center gap-3 p-4" data-testid="admin-security-team-row">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                {u.name || "—"}
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${ROLE_BADGES[u.role] || "bg-slate-100 text-slate-500"}`}>
                  {ROLE_LABELS[u.role] || u.role}
                </span>
              </p>
              <p className="text-xs text-slate-500">{u.email}</p>
            </div>
            {u.role !== "super_admin" && u.id !== me?.id && (
              <select value={u.role} disabled={busyId === u.id}
                onChange={(e) => changeRole(u.id, e.target.value)}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] font-semibold text-slate-600 focus:outline-none"
                data-testid="admin-security-role-select">
                <option value="user">Utilisateur</option>
                <option value="moderator">Modérateur</option>
                <option value="admin">Admin</option>
              </select>
            )}
            {u.role === "super_admin" && <ShieldCheck className="h-4 w-4 text-bleu" />}
          </div>
        ))}
        <p className="text-[11px] text-slate-400">
          Pour nommer un nouvel admin ou modérateur, utilisez la page Utilisateurs (sélecteur de rôle). Chaque changement est tracé au journal.
        </p>
      </section>

      <section className="card-integra p-5" data-testid="admin-security-permissions">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <History className="h-4 w-4 text-bleu" /> Permissions par rôle (appliquées côté serveur)
        </h2>
        <div className="mt-3 space-y-2.5">
          {Object.entries(data.roles).map(([role, perms]) => (
            <div key={role} className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs font-bold text-slate-700">{ROLE_LABELS[role] || role}</p>
              <p className="mt-1 text-[11px] text-slate-500">{perms.join(" · ")}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
